import type { CrmDisplayConfigAddress, CrmDisplayConfigField } from '@plato/data/model/crm-config';
import { defaultDisplayConfigs } from '@plato/data/model/crm-config';
import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { Dialog } from '@plato/ui/containers/dialog';
import { cn } from '@plato/ui/util/classes';
import { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerAddressGetDetailOutput } from '../../../server/api/routers/plato-user/query/customer-address.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useCrmConfig } from '../hooks/use-crm-config.ts';
import type { CrmContext } from './crm-context.tsx';
import { CrmContextProvider, useCrmFieldConfig, useDraftData } from './crm-context.tsx';
import { CrmFieldValue, getFieldKey } from './crm-field.tsx';
import { CrmFieldLabel } from './crm-label.tsx';

export type EditAddressDialogProps = {
    addressId: string;
    customerId: string;
};

export const EditAddressDialog = DialogStateManager.create<EditAddressDialogProps>(
    ({ addressId, customerId }) => {
        const { remove: closeDialog, visible: isDialogVisible } = useDialog();
        const { toast } = useToast();
        const { t } = useTranslation('customer-detail');
        const sendEvent = useAnalytics();
        const { data: crmConfig } = useCrmConfig();
        const { crmCustomFieldsConfig, crmDisplayConfig } = crmConfig ?? {};
        const apiUtils = api.useUtils();

        const { data: apiData, isLoading } = api.platoUser.query.customerAddress.getDetail.useQuery({
            addressId,
            customerId,
        });

        const { mutate: apiSaveAddress, isPending } =
            api.platoUser.command.customers.updateAddress.useMutation({
                onError: async () => {
                    toast({ icon: 'warning', title: t('save_error') });
                    reset();
                },

                onSuccess: async () => {
                    toast({ icon: 'success', title: t('save_successful') });
                    void apiUtils.platoUser.query.customerAddress.getDetail.invalidate();
                    void apiUtils.platoUser.query.customerAddress.listDetail.invalidate({
                        customerId,
                        types: [SrCustomerAddressType.SHIPPING],
                    });

                    sendEvent('customer_address_edited', {
                        customer_id: customerId,
                        address_id: addressId,
                    });

                    closeDialog();
                },
            });

        const handleSave = useCallback(
            (data: CustomerAddressGetDetailOutput) => {
                apiSaveAddress({
                    addressId: data.addressId,
                    customerId: data.customerId,
                    canReceiveGoodsAtNight: data.canReceiveGoodsAtNight,
                    canReceiveParcels: data.canReceiveParcels,
                    comment: data.comment || undefined,
                    isPreferredShippingAddress: data.isPreferredShippingAddress,
                    customFields: data.customFields || undefined,
                });
            },
            [apiSaveAddress],
        );

        const { data, reset, save, setData } = useDraftData<CustomerAddressGetDetailOutput>({
            data: apiData,
            disableAutoSave: true,
            save: handleSave,
        });

        const context = useMemo(
            (): CrmContext<'address'> => ({
                data,
                entityType: 'address',
                customFieldConfig: crmCustomFieldsConfig,
                setData,
            }),
            [data, crmCustomFieldsConfig, setData],
        );

        const config: CrmDisplayConfigAddress = crmDisplayConfig?.address ?? defaultDisplayConfigs.address;
        const fields = config.editFields ?? config.fields ?? [];
        const anyEditable = fields.some((x) => x.writable);

        return (
            <Dialog open={isDialogVisible} onClose={closeDialog} disablePointerDismissal>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header
                        title={t('address_edit__modal_title')}
                        subtitle={t('address_edit__modal_subtitle')}
                    />
                    <DialogForm.Content>
                        <CrmContextProvider value={context}>
                            <fieldset
                                className="grid grid-cols-2 gap-4 w-full p-0"
                                disabled={isLoading || isPending}
                            >
                                {fields.map((field) => (
                                    <CrmField key={getFieldKey(field)} config={field} />
                                ))}
                            </fieldset>
                        </CrmContextProvider>
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={closeDialog}
                        onSubmit={save}
                        submitText={t('address_edit__action__save')}
                        disabled={!anyEditable || isLoading}
                        isLoading={isPending}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);

type CrmFieldProps = {
    config: CrmDisplayConfigField;
};

function CrmField(props: CrmFieldProps) {
    const config = useCrmFieldConfig(props.config);
    const id = useId();
    const isCompact = config.compact ?? false;

    return (
        <div className={cn('flex flex-col gap-2', !isCompact && 'col-span-2')}>
            <CrmFieldLabel htmlFor={id}>{config.label}</CrmFieldLabel>
            <CrmFieldValue config={config} id={id} mode="edit" />
        </div>
    );
}
